import React, { createContext, useEffect, useState } from 'react';
import { endpoints } from '../../../config/connections';
import callApi from '../utilities/callApi';

const isCategoryShape = (data) => (
  Array.isArray(data)
  && data.every((entry) => (
    entry
    && typeof entry === 'object'
    && typeof entry.key === 'string'
    && typeof entry.title === 'string'
    && Array.isArray(entry.items)
  ))
);

const featureNameToTitle = (featureName = '') => {
  const withoutSuffix = featureName.replace(/_CATEGORY$/i, '');
  return withoutSuffix
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .trim() + ' Category';
};

const featureNameToKey = (featureName = '') => {
  const normalized = featureName.replace(/_CATEGORY$/i, '').toLowerCase();
  const parts = normalized.split('_').filter(Boolean);
  if (!parts.length) {
    return '';
  }

  const [first, ...rest] = parts;
  return `${first}${rest.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('')}Category`;
};

export const formatFeatureCategoryResponse = (apiCategories = []) => {
  if (!Array.isArray(apiCategories)) {
    return [];
  }

  return apiCategories.reduce((acc, entry) => {
    const featureName = typeof entry?.featureName === 'string' ? entry.featureName : '';
    const key = featureNameToKey(featureName);
    if (!key) {
      return acc;
    }

    acc.push({
      title: featureNameToTitle(featureName),
      key,
      featureName: typeof entry?.featureName === 'string' ? entry.featureName : '',
      description: typeof entry?.description === 'string' ? entry.description : '',
      items: Array.isArray(entry?.allowedCategories) ? entry.allowedCategories : [],
    });

    return acc;
  }, []);
};

export const FeatureCategoryContext = createContext({
  categories: [],
  setCategories: (_nextValue) => {},
  loading: false,
  error: null,
  refreshCategories: () => Promise.resolve([]),
});

const FeatureCategoryContextProvider = (props) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategoryData = () => {
    setLoading(true);
    setError(null);

    return callApi
      .getApiDataLoggedUser(`${endpoints.apigee}/document-management/coreservice/user-management/v1/featureCategory/getActive`)
      .then((response) => {
        const activeCategories = response?.data;
        const formattedCategories = formatFeatureCategoryResponse(activeCategories);

        if (isCategoryShape(formattedCategories)) {
          setCategories(formattedCategories);
        } else {
          setCategories([]);
        }
        setLoading(false);
        return formattedCategories;
      })
      .catch((fetchError) => {
        setError(fetchError);
        setLoading(false);
        setCategories([]);
        return [];
      });
  };

  useEffect(() => {
    if (sessionStorage.getItem('tok')) {
      fetchCategoryData();
    }
  }, []);

  return (
    <FeatureCategoryContext.Provider
      value={{
        categories,
        setCategories,
        loading,
        error,
        refreshCategories: fetchCategoryData,
      }}
    >
      {props.children}
    </FeatureCategoryContext.Provider>
  );
};

export default FeatureCategoryContextProvider;
